/**
 * Workshops (1.5.1): the tech's proposals on Events (shown only when there is one waiting) with Publish — the place,
 * the cap and who runs it, then it is a workshop on Events with Invite all tenants — or Decline (kept, never deleted);
 * and the tech's own Propose a workshop sheet (a title, what it is, when — nothing is sent from it).
 */
import { useRef, useState, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { addDaysToKey } from '@shared/dates';
import { WORKSHOP_RUNNERS, type WorkshopProposalView, type WorkshopRunner } from '@shared/activities';
import { minutesLabel } from '@shared/volunteers';
import { useAnnounce } from '@/components/Announcer';
import { Icon } from '@/components/Icons';
import { StepHead, SummaryCard } from '@/components/Steps';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { useToast } from '@/components/ui/use-toast';
import { errorText } from '@/features/people/api';
import { SelectField, TextArea, TextField } from '@/features/people/fields';
import { SpanishWording, useSpanishPeople } from '@/features/messages/SpanishWording';
import { formatDayMedium } from '@/lib/dates';
import { postJson, useActivityRefresh, useMyProposals, useProposals } from './api';

const TIMES = Array.from({ length: 31 }, (_, i) => 7 * 60 + i * 30);
const iso = (k: string) => `${k.slice(0, 4)}-${k.slice(4, 6)}-${k.slice(6, 8)}`;

/** Events → Workshop proposals (only when one is waiting). */
export function WorkshopProposals() {
  const { t, i18n } = useTranslation();
  const list = useProposals();
  const [pub, setPub] = useState<WorkshopProposalView | null>(null);
  const opener = useRef<HTMLElement | null>(null);
  const waiting = (list.data?.proposals ?? []).filter((p) => p.state === 'waiting');
  if (!waiting.length) return null;
  return (
    <Card as="section" aria-labelledby="h-ws-proposals" style={{ marginTop: 18 }} data-testid="workshop-proposals">
      <CardHeader>
        <CardTitle id="h-ws-proposals">{t('workshops.proposals')}</CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="needs">
          {waiting.map((p) => (
            <li key={p.proposalId} data-testid={`proposal-${p.proposalId}`}>
              <span className="status hold">
                <Icon name="clock" aria-hidden="true" /> {t('workshops.state.waiting')}
              </span>
              <div>
                <b>{p.title}</b>
                <span>{t('workshops.proposalLine', { day: formatDayMedium(p.dayKey, i18n.language), time: minutesLabel(p.minutes), by: p.proposedBy })}</span>
                <span>{p.what}</span>
                <div className="chips">
                  <Button
                    size="sm"
                    onClick={(e) => {
                      opener.current = e.currentTarget;
                      setPub(p);
                    }}
                    data-testid={`publish-${p.proposalId}`}
                  >
                    {t('workshops.publish')}
                  </Button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </CardContent>
      <Sheet open={!!pub} onOpenChange={(o) => (!o ? setPub(null) : undefined)}>
        {pub ? <PublishSheet p={pub} onClose={() => setPub(null)} returnFocusTo={opener} /> : null}
      </Sheet>
    </Card>
  );
}

function PublishSheet({ p, onClose, returnFocusTo }: { p: WorkshopProposalView; onClose: () => void; returnFocusTo: RefObject<HTMLElement | null> }) {
  const { t, i18n } = useTranslation();
  const announce = useAnnounce();
  const toast = useToast();
  const refresh = useActivityRefresh();
  const [place, setPlace] = useState('');
  const [cap, setCap] = useState('12');
  const [runBy, setRunBy] = useState<WorkshopRunner>('tech');
  const [runner, setRunner] = useState('');
  const [details, setDetails] = useState(p.what);
  const spanishPeople = useSpanishPeople();
  const [detailsEs, setDetailsEs] = useState('');
  const [declining, setDeclining] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const run = async (fn: () => Promise<string>) => {
    setBusy(true);
    setProblem(null);
    try {
      const m = await fn();
      toast(m);
      announce(m);
      refresh();
      onClose();
    } catch (e) {
      const m = errorText(e);
      setProblem(m);
      announce(m, 'assertive');
    } finally {
      setBusy(false);
    }
  };
  const publish = () => {
    if (!place.trim()) return setProblem(t('events.new.needRoom'));
    const n = Math.max(0, Math.floor(Number(cap.replace(/\D/g, '')) || 0));
    if (!n) return setProblem(t('workshops.needCap'));
    void run(async () => {
      await postJson(`/api/workshops/proposals/${encodeURIComponent(p.proposalId)}/publish`, { place: place.trim(), cap: n, runBy, runnerName: runner.trim(), details: details.trim(), detailsEs: detailsEs.trim() });
      return t('workshops.published', { title: p.title });
    });
  };
  return (
    <SheetContent
      title={t('workshops.publishTitle', { title: p.title })}
      returnFocusTo={returnFocusTo}
      footer={
        declining ? (
          <>
            <Button variant="secondary" onClick={() => setDeclining(false)}>
              {t('activities.back')}
            </Button>
            <Button disabled={busy} onClick={() => run(async () => (await postJson(`/api/workshops/proposals/${encodeURIComponent(p.proposalId)}/decline`, { note }), t('workshops.declined')))} data-testid="ws-decline-save">
              {t('workshops.decline')}
            </Button>
          </>
        ) : (
          <>
            <Button variant="secondary" onClick={() => setDeclining(true)} data-testid="ws-decline">
              {t('workshops.decline')}
            </Button>
            <Button disabled={busy} onClick={publish} data-testid="ws-publish">
              {t('workshops.publish')}
            </Button>
          </>
        )
      }
    >
      <p className="help">
        {formatDayMedium(p.dayKey, i18n.language)} · {minutesLabel(p.minutes)} · {p.proposedBy}
      </p>
      {declining ? (
        <TextArea id="ws-note" label={t('workshops.declineWhy')} value={note} onChange={setNote} />
      ) : (
        <>
          <TextField id="ws-pub-place" label={t('events.new.room')} required value={place} onChange={setPlace} placeholder={t('workshops.placePh')} />
          <TextField id="ws-pub-cap" label={t('workshops.cap')} required inputMode="numeric" value={cap} onChange={setCap} help={t('workshops.capHelp')} />
          <fieldset className="field">
            <legend className="eyebrow">{t('workshops.runBy')}</legend>
            <div className="seg" role="group" aria-label={t('workshops.runBy')}>
              {WORKSHOP_RUNNERS.map((r) => (
                <button key={r} type="button" aria-pressed={runBy === r} onClick={() => setRunBy(r)}>
                  {t(`workshops.run.${r}`)}
                </button>
              ))}
            </div>
          </fieldset>
          <TextField id="ws-pub-runner" label={t('workshops.runnerName')} value={runner} onChange={setRunner} />
          <TextArea id="ws-pub-details" label={t('workshops.details')} value={details} onChange={setDetails} />
          <SpanishWording count={spanishPeople} value={detailsEs} onChange={setDetailsEs} rows={3} maxLength={2000} testId="ws-pub-details-es" />
          <p className="help">{t('workshops.publishNote')}</p>
        </>
      )}
      {problem ? (
        <p className="field-err" role="alert">
          <Icon name="alert" width={16} height={16} /> {problem}
        </p>
      ) : null}
    </SheetContent>
  );
}

/** The tech's Today: Propose a workshop (one press) and their own proposals. */
export function TechWorkshops({ todayKey }: { todayKey: string }) {
  const { t, i18n } = useTranslation();
  const mine = useMyProposals();
  const [open, setOpen] = useState(false);
  const opener = useRef<HTMLElement | null>(null);
  const list = mine.data?.proposals ?? [];
  return (
    <Card as="section" aria-labelledby="h-tech-ws" style={{ marginTop: 18 }} data-testid="tech-workshops">
      <CardHeader>
        <CardTitle id="h-tech-ws">{t('workshops.proposeTitle')}</CardTitle>
        <Button
          size="sm"
          variant="secondary"
          onClick={(e) => {
            opener.current = e.currentTarget;
            setOpen(true);
          }}
          data-testid="open-propose"
        >
          <Icon name="plus" /> {t('workshops.propose')}
        </Button>
      </CardHeader>
      <CardContent>
        <p className="help">{t('workshops.proposeLead')}</p>
        {list.length ? (
          <ul className="needs" aria-label={t('workshops.mine')}>
            {list.map((p) => (
              <li key={p.proposalId} data-testid={`mine-${p.proposalId}`}>
                <span className={`status ${p.state === 'published' ? 'completed' : p.state === 'declined' ? 'cancelled' : 'hold'}`}>{t(`workshops.state.${p.state}`)}</span>
                <div>
                  <b>{p.title}</b>
                  <span>
                    {formatDayMedium(p.dayKey, i18n.language)} · {minutesLabel(p.minutes)}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        ) : null}
      </CardContent>
      <Sheet open={open} onOpenChange={(o) => (!o ? setOpen(false) : undefined)}>
        {open ? <ProposeSheet onClose={() => setOpen(false)} returnFocusTo={opener} todayKey={todayKey} /> : null}
      </Sheet>
    </Card>
  );
}

const PROPOSE_STEPS = ['what', 'when', 'confirm'] as const;

function ProposeSheet({ onClose, returnFocusTo, todayKey }: { onClose: () => void; returnFocusTo: RefObject<HTMLElement | null>; todayKey: string }) {
  const { t, i18n } = useTranslation();
  const announce = useAnnounce();
  const toast = useToast();
  const refresh = useActivityRefresh();
  const [step, setStep] = useState<(typeof PROPOSE_STEPS)[number]>('what');
  const [title, setTitle] = useState('');
  const [what, setWhat] = useState('');
  const [day, setDay] = useState(iso(addDaysToKey(todayKey, 7)));
  const [minutes, setMinutes] = useState(11 * 60);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const dayKey = day.replace(/-/g, '');
  const n = PROPOSE_STEPS.indexOf(step) + 1;
  const next = () => {
    setProblem(null);
    if (step === 'what') {
      if (!title.trim()) return setProblem(t('workshops.needTitle'));
      if (!what.trim()) return setProblem(t('workshops.needWhat'));
      return setStep('when');
    }
    if (!/^\d{8}$/.test(dayKey)) return setProblem(t('activities.needDate'));
    setStep('confirm');
  };
  const send = async () => {
    setBusy(true);
    setProblem(null);
    try {
      await postJson('/api/workshops/propose', { title: title.trim(), what: what.trim(), dayKey, minutes });
      const m = t('workshops.proposed');
      toast(m);
      announce(m);
      refresh();
      onClose();
    } catch (e) {
      const m = errorText(e);
      setProblem(m);
      announce(m, 'assertive');
    } finally {
      setBusy(false);
    }
  };
  return (
    <SheetContent
      title={t('workshops.propose')}
      returnFocusTo={returnFocusTo}
      data-testid="propose-sheet"
      footer={
        <>
          {step === 'what' ? (
            <Button variant="secondary" onClick={onClose}>
              {t('activities.cancel')}
            </Button>
          ) : (
            <Button variant="secondary" onClick={() => setStep(PROPOSE_STEPS[n - 2]!)} data-testid="propose-back">
              {t('activities.back')}
            </Button>
          )}
          {step === 'confirm' ? (
            <Button disabled={busy} onClick={() => send()} data-testid="propose-send">
              {t('workshops.proposeSend')}
            </Button>
          ) : (
            <Button onClick={next} data-testid="propose-next">
              {t('activities.next')}
            </Button>
          )}
        </>
      }
    >
      <StepHead n={n} of={PROPOSE_STEPS.length} question={t(`workshops.q.${step}`)} id="propose-q" />
      {step === 'what' ? (
        <>
          <TextField id="pr-title" label={t('workshops.title')} required value={title} onChange={setTitle} placeholder={t('workshops.titlePh')} maxLength={120} />
          <TextArea id="pr-what" label={t('workshops.what')} required value={what} onChange={setWhat} maxLength={1000} />
        </>
      ) : step === 'when' ? (
        <div className="grid-2">
          <TextField id="pr-date" label={t('events.new.date')} type="date" required value={day} onChange={setDay} />
          <SelectField id="pr-time" label={t('events.new.time')} value={String(minutes)} onChange={(v) => setMinutes(Number(v))} options={TIMES.map((m) => ({ value: String(m), label: minutesLabel(m) }))} />
        </div>
      ) : (
        <SummaryCard
          testId="propose-confirm"
          rows={[
            { label: t('workshops.c.what'), value: title.trim() },
            { label: t('workshops.c.details'), value: what.trim() },
            { label: t('workshops.c.when'), value: `${/^\d{8}$/.test(dayKey) ? formatDayMedium(dayKey, i18n.language) : '—'} · ${minutesLabel(minutes)}` },
          ]}
        >
          <p className="help">{t('workshops.proposeLead')}</p>
        </SummaryCard>
      )}
      {problem ? (
        <p className="field-err" role="alert" data-testid="propose-problem">
          <Icon name="alert" width={16} height={16} /> {problem}
        </p>
      ) : null}
    </SheetContent>
  );
}
